import {
  PDF_PLACEHOLDER,
  PLACEHOLDER,
  toExternalMediaInfo,
  toMediaInfo,
  toPdfMediaInfo,
  toVideoMediaInfo,
  type MediaInfo,
} from "@/features/media/mediaInfo";
import {
  imageSize,
  imageUrl,
  logoUrl,
  type MediaContext,
} from "@/features/media/mediaUrl";
import type {
  ItemProperties,
  MediaEntry,
  MediaRef,
  ProjectConfigs,
} from "@/types";

/**
 * Grouping a POI's media into the buttons shown under its details.
 *
 * Pure, so the grouping rules — which config flags fold the main image in, where
 * PDFs come from, how a video resolves its poster — are testable without a
 * gallery on screen.
 */

export const GALLERY_TYPES = [
  "photo",
  "pdf",
  "video",
  "3d",
  "street_view",
  "web",
] as const;

export type GalleryType = (typeof GALLERY_TYPES)[number];

export interface GalleryGroup {
  type: GalleryType;
  items: MediaInfo[];
}

/** The `medias` entries of one type; absent on every tenant schema so far. */
function entriesOfType(medias: MediaEntry[], type: string): MediaEntry[] {
  return medias.filter((entry) => entry.type === type);
}

export function poiGalleries(
  properties: ItemProperties | undefined,
  configs: ProjectConfigs,
  media: MediaContext
): GalleryGroup[] {
  const mediasets = properties?.mediasets;
  const medias = mediasets?.medias ?? [];

  const photos = (mediasets?.gallery ?? [])
    .map((image) => toMediaInfo(image, media))
    .filter((info): info is MediaInfo => info !== null);

  // The header image is also the first gallery slide when the tenant asks for it
  // — some projects have a single photo and would otherwise get no gallery.
  if (configs.includeMainImageInGallery) {
    const main = toMediaInfo(mediasets?.main, media);
    if (main) photos.push(main);
  }

  // Legacy called `.concat()` and dropped the result, so this flag never did
  // anything; it also collected raw refs rather than slides, so it could not have.
  if (configs.includeMediaThumbnailsInGallery) {
    for (const entry of medias) {
      const info = toMediaInfo(entry.image, media);
      if (info) photos.push(info);
    }
  }

  const pdfs = [
    ...asRefs(properties?.pdfs)
      .map((ref) => toPdfMediaInfo(ref, media))
      .filter((info): info is MediaInfo => info !== null),
    ...entriesOfType(medias, "pdf").map((entry) =>
      toExternalMediaInfo(entry, PDF_PLACEHOLDER, media)
    ),
  ];

  const groups: GalleryGroup[] = [
    { type: "photo", items: photos },
    { type: "pdf", items: pdfs },
    {
      type: "video",
      items: entriesOfType(medias, "video").map((entry) =>
        toVideoMediaInfo(entry, media)
      ),
    },
    ...(["3d", "street_view", "web"] as const).map((type) => ({
      type,
      items: entriesOfType(medias, type).map((entry) =>
        toExternalMediaInfo(entry, PLACEHOLDER, media)
      ),
    })),
  ];

  return groups.filter((group) => group.items.length > 0);
}

/** A POI's `pdfs` field, which the sync mirrors in place as stored references. */
function asRefs(value: unknown): MediaRef[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is MediaRef => typeof entry === "object" && entry !== null
  );
}

/** The icon-font glyph for each media type; street view draws its own mark. */
export const GALLERY_ICONS: Record<string, string> = {
  photo: "fontonicons_Photo-11",
  video: "linearicons_camera-video",
  "3d": "clichy_3d",
  web: "ios_Webpage",
  pdf: "material_ic_picture_as_pdf_24px",
};

const CARD_THUMBNAIL_SIZE = imageSize(512, 512);
const CARD_ICON_SIZE = imageSize(20, 20);

/**
 * The picture and the icon override of one gallery card.
 *
 * A tenant can override both per type through the project-level `medias` config —
 * a cover image and an icon — which is how a project puts its own artwork on the
 * "3D tour" card instead of the first slide. Without a cover the first slide
 * stands in, then the tenant's logo.
 */
export function galleryCardImages(
  group: GalleryGroup,
  configs: ProjectConfigs,
  media: MediaContext
): { thumbnail: string | null; icon: string | null } {
  const typeConfig = configs.medias?.find((entry) => entry.type === group.type);

  return {
    thumbnail:
      imageUrl(typeConfig?.image?.image, CARD_THUMBNAIL_SIZE, media) ??
      group.items[0]?.original ??
      logoUrl(configs.appLogos?.[0]?.image?.fileName, media.domain),
    icon: imageUrl(typeConfig?.icon?.image, CARD_ICON_SIZE, media),
  };
}
